import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, GeoJSON } from "react-leaflet";
import { List, Map as MapIcon, X } from "lucide-react";
import { US_STATES } from "@/data/states";
import { emojiFor } from "@/data/issues";

const NAME_TO_CODE = Object.fromEntries(US_STATES.map(([c, n]) => [n, c]));

function shade(count, max) {
  if (!count) return { fillColor: "#3a1420", fillOpacity: 0.5 };
  const t = Math.max(0.25, count / max);
  return { fillColor: t > 0.66 ? "#E02189" : t > 0.33 ? "#AD1C5D" : "#7A1440", fillOpacity: 0.55 + t * 0.4 };
}

function StateDetail({ state, onClose }) {
  const max = state.issues[0]?.count || 1;
  return (
    <div className="state-detail" data-testid="state-detail">
      <div className="sd-head">
        <div>
          <div className="sd-name">{state.name}{state.south && <span className="south-tag">South</span>}</div>
          <div className="sd-sub"><b data-testid="state-detail-count">{state.count}</b> pledge{state.count === 1 ? "" : "s"} · what kids here care about most</div>
        </div>
        <button className="sd-close" onClick={onClose} aria-label="Close" data-testid="state-detail-close"><X size={14} /></button>
      </div>
      {state.issues.map((it, i) => (
        <div className="bar-row" key={it.issue} data-testid={`state-issue-${it.issue}`}>
          <span className="bl" style={{ width: 170 }}><span className="rank">{i + 1}</span>{emojiFor(it.issue)} {it.issue}</span>
          <span className="bar-track"><span className="bar-fill" style={{ width: (it.count / max) * 100 + "%", background: "linear-gradient(90deg,#67DAD7,#E02189)" }} /></span>
          <span className="bn">{it.count}</span>
        </div>
      ))}
      {state.issues.length === 0 && <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13 }}>No issues recorded for this state yet.</div>}
    </div>
  );
}

export default function StateBreakdown({ byState = [], known = 0, total = 0 }) {
  const [view, setView] = useState("list");
  const [geo, setGeo] = useState(null);
  const [selected, setSelected] = useState(null);
  const selRef = useRef(null);
  const counts = useMemo(() => Object.fromEntries(byState.map((s) => [s.state, s])), [byState]);
  const max = byState[0]?.count || 1;
  const detail = selected ? counts[selected] || { state: selected, name: US_STATES.find(([c]) => c === selected)?.[1] || selected, count: 0, issues: [], south: false } : null;

  useEffect(() => {
    if (view === "map" && !geo) fetch("/us-states.json").then((r) => r.json()).then(setGeo).catch((e) => console.warn("States geojson failed", e));
  }, [view, geo]);

  const baseStyle = (code) => ({ color: "#FDF71E", weight: 0.6, opacity: 0.35, ...shade(counts[code]?.count || 0, max) });
  const selStyle = { color: "#67DAD7", weight: 3, opacity: 1 };
  const style = (f) => {
    const code = NAME_TO_CODE[f.properties.name];
    return code === selected ? { ...baseStyle(code), ...selStyle } : baseStyle(code);
  };
  const onEach = (f, layer) => {
    const code = NAME_TO_CODE[f.properties.name];
    const c = counts[code]?.count || 0;
    layer.bindTooltip(`<b>${f.properties.name}</b><br/>${c} pledge${c === 1 ? "" : "s"} · click for issues`, { sticky: true, className: "state-tip" });
    layer.on({
      click: () => {
        if (selRef.current && selRef.current !== layer) selRef.current.setStyle(baseStyle(NAME_TO_CODE[selRef.current.feature.properties.name]));
        layer.setStyle({ ...baseStyle(code), ...selStyle });
        layer.bringToFront();
        selRef.current = layer;
        setSelected(code);
      },
      mouseover: (e) => { if (code !== selected) e.target.setStyle({ weight: 2, opacity: 1 }); },
      mouseout: (e) => { if (selRef.current !== e.target) e.target.setStyle({ weight: 0.6, opacity: 0.35 }); },
    });
  };

  const clear = () => {
    if (selRef.current) { selRef.current.setStyle(baseStyle(NAME_TO_CODE[selRef.current.feature.properties.name])); selRef.current = null; }
    setSelected(null);
  };

  return (
    <div className="panel" data-testid="state-breakdown-panel">
      <div className="map-head">
        <h3 style={{ margin: 0 }}>Pledges by State</h3>
        <div className="seg" role="tablist">
          <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} data-testid="state-view-list"><List size={13} /> List</button>
          <button className={view === "map" ? "on" : ""} onClick={() => setView("map")} data-testid="state-view-map"><MapIcon size={13} /> Map</button>
        </div>
      </div>
      <div className="sub-note"><b data-testid="states-known">{known}</b> of {total} pledges have a known state · <span className="south-tag">South</span> = SBGWC focus states · click a state for its top issues</div>
      {view === "list" ? (
        <div data-testid="state-list">
          {byState.map((s, i) => (
            <button className={`bar-row state-row ${selected === s.state ? "sel" : ""}`} key={s.state} onClick={() => setSelected(selected === s.state ? null : s.state)} data-testid={`state-row-${s.state}`}>
              <span className="bl" style={{ width: 190 }}><span className="rank">{i + 1}</span>{s.name}{s.south && <span className="south-tag">South</span>}</span>
              <span className="bar-track"><span className="bar-fill" style={{ width: (s.count / max) * 100 + "%", background: s.south ? "linear-gradient(90deg,#AD1C5D,#E02189)" : "rgba(255,255,255,.35)" }} /></span>
              <span className="bn">{s.count}</span>
            </button>
          ))}
          {byState.length === 0 && <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13 }}>No state data yet.</div>}
        </div>
      ) : (
        <div className="map-wrap choro" data-testid="state-map">
          <MapContainer center={[37.5, -96]} zoom={4} scrollWheelZoom={false} zoomControl={false} dragging={false} doubleClickZoom={false} attributionControl={false} style={{ height: "100%", width: "100%", background: "transparent" }}>
            {geo && <GeoJSON data={geo} style={style} onEachFeature={onEach} />}
          </MapContainer>
          <div className="choro-legend"><span><i style={{ background: "#3a1420" }} /> 0</span><span><i style={{ background: "#7A1440" }} /> low</span><span><i style={{ background: "#AD1C5D" }} /> mid</span><span><i style={{ background: "#E02189" }} /> high</span></div>
        </div>
      )}
      {detail && <StateDetail state={detail} onClose={clear} />}
    </div>
  );
}
